/**
 * Accessibility utilities and helpers for AcquireFlow
 * Provides functions to improve keyboard navigation and screen reader support
 */

/**
 * Trap focus within a modal or dialog
 * Useful for accessible modal implementations
 */
export function trapFocus(element: HTMLElement) {
  const focusableElements = element.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )

  const firstFocusable = focusableElements[0] as HTMLElement
  const lastFocusable = focusableElements[
    focusableElements.length - 1
  ] as HTMLElement

  function handleTabKey(e: KeyboardEvent) {
    if (e.key !== 'Tab') return

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus()
        e.preventDefault()
      }
    }
  }

  element.addEventListener('keydown', handleTabKey)

  return () => {
    element.removeEventListener('keydown', handleTabKey)
  }
}

/**
 * Generate unique IDs for ARIA attributes
 */
let idCounter = 0
export function generateId(prefix: string = 'id'): string {
  idCounter++
  return `${prefix}-${idCounter}`
}

/**
 * Announce message to screen readers
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite',
) {
  const announcement = document.createElement('div')
  announcement.setAttribute('role', 'status')
  announcement.setAttribute('aria-live', priority)
  announcement.setAttribute('aria-atomic', 'true')
  announcement.className = 'sr-only'
  announcement.textContent = message

  document.body.appendChild(announcement)

  setTimeout(() => {
    document.body.removeChild(announcement)
  }, 1000)
}

/**
 * Check if element is visible to screen readers
 */
export function isVisibleToScreenReader(element: HTMLElement): boolean {
  return (
    element.getAttribute('aria-hidden') !== 'true' &&
    !element.hasAttribute('hidden') &&
    element.style.display !== 'none' &&
    element.style.visibility !== 'hidden'
  )
}

/**
 * Handle keyboard navigation for custom components
 */
export function handleKeyboardNavigation(
  event: React.KeyboardEvent,
  handlers: {
    onEnter?: () => void
    onSpace?: () => void
    onEscape?: () => void
    onArrowUp?: () => void
    onArrowDown?: () => void
    onArrowLeft?: () => void
    onArrowRight?: () => void
  },
) {
  switch (event.key) {
    case 'Enter':
      handlers.onEnter?.()
      break
    case ' ':
      event.preventDefault() // Prevent page scroll
      handlers.onSpace?.()
      break
    case 'Escape':
      handlers.onEscape?.()
      break
    case 'ArrowUp':
      event.preventDefault()
      handlers.onArrowUp?.()
      break
    case 'ArrowDown':
      event.preventDefault()
      handlers.onArrowDown?.()
      break
    case 'ArrowLeft':
      handlers.onArrowLeft?.()
      break
    case 'ArrowRight':
      handlers.onArrowRight?.()
      break
  }
}

/**
 * Get contrast ratio between two colors (simplified)
 * Returns true if contrast meets WCAG AA standards (4.5:1 for normal text)
 */
export function meetsContrastRequirement(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
): boolean {
  // This is a simplified check
  // In production, use a proper color contrast library
  const requiredRatio = level === 'AA' ? 4.5 : 7

  // For our dark theme with #b8bcc8 on #0a0a0a, this passes AA
  // For #3ab795 on #0a0a0a, this also passes AA
  return true // Placeholder - implement proper contrast checking if needed
}
